import type {
  TesterQueueResponse,
  ActiveJobInfo,
  TesterPresenceItem,
  TesterPresenceTimingConfig,
  DiscordActivityFloorManagerOnDuty,
  DiscordActivityStuckJob,
} from '@runhuman/shared';
import type { InstanceParticipant } from '../use-instance-participants';

/**
 * Fixture data for the dev `?preview=` harness.
 *
 * Shapes match the live contract types — same field names, same nullability —
 * so a developer eyeballing the preview at `localhost:3200/?preview=tester`
 * sees what production will render against real backend data, not a
 * hand-waved approximation.
 */

const NOW = Date.now();
const ago = (minutes: number) => new Date(NOW - minutes * 60_000).toISOString();
const future = (minutes: number) => new Date(NOW + minutes * 60_000).toISOString();

export const fixtureQueue: TesterQueueResponse = {
  available: [
    {
      id: 'job-aaaa',
      url: 'https://example-saas.com/onboarding',
      description: 'Walk through the onboarding flow and report any blockers.',
      priorityScore: 100,
      deviceClass: 'desktop',
      targetDurationMinutes: 8,
      createdAt: ago(2),
      expiresAt: future(28),
      status: 'waiting',
      organizationName: 'Acme Inc',
      projectName: 'Acme Web',
    },
    {
      id: 'job-bbbb',
      url: 'https://demo-shop.com/checkout',
      description: 'Verify the checkout flow with a test card.',
      priorityScore: 40,
      deviceClass: 'desktop',
      targetDurationMinutes: 12,
      createdAt: ago(11),
      expiresAt: future(19),
      status: 'waiting',
    },
    {
      id: 'job-cccc',
      url: 'https://mobile-app.example/signup',
      description: 'Sign up flow on a mid-range Android device.',
      priorityScore: 0,
      deviceClass: 'mobile',
      targetDurationMinutes: 6,
      createdAt: ago(34),
      expiresAt: future(56),
      status: 'waiting',
    },
  ],
  inProgress: [],
  needsLateSubmission: [],
  orgCompletedCounts: {},
};

export const fixtureActiveJob: ActiveJobInfo = {
  id: 'job-active',
  url: 'https://runhuman.com/dashboard',
  description: 'Audit the dashboard layout on a 1440px viewport.',
  priorityScore: 100,
  deviceClass: 'desktop',
  targetDurationMinutes: 10,
  createdAt: ago(8),
  expiresAt: future(22),
  status: 'working',
  claimedAt: ago(3),
  testerAlias: 'preview-self',
  testerToken: 'preview-tester-token',
};

/**
 * Six tester rows engineered to land in each of the six presence buckets
 * once `getMenuGroup()` runs against `fixturePresenceTiming` — gives the
 * preview harness coverage of every bucket header + status dot color
 * without needing a live database. Field shapes mirror the `/tester/presence`
 * response contract exactly.
 */
export const fixturePresence: readonly TesterPresenceItem[] = [
  // online — on-shift, recent activity, no active job
  {
    id: 'tester-online',
    alias: 'morgan',
    avatarUrl: null,
    availability: 'online',
    activeJob: null,
    scheduleState: 'on-shift',
    lastActiveAt: ago(0),
    isFloorManager: true,
    devices: ['ios', 'android', 'pc', 'mac'],
    onBreakUntil: null,
  },
  // busy — derived from activeJob != null
  {
    id: 'tester-busy',
    alias: 'sasha',
    avatarUrl: null,
    availability: 'online',
    activeJob: {
      jobId: 'job-active-fixture',
      url: 'https://example-saas.com/onboarding',
      description: 'Walk through the onboarding flow.',
      claimedAt: ago(4),
      project: { name: 'Acme Web', organizationName: 'Acme Inc' },
    },
    scheduleState: 'on-shift',
    lastActiveAt: ago(1),
    isFloorManager: false,
    devices: ['ios', 'android'],
    onBreakUntil: null,
  },
  // away — stored availability, recent enough that no derivation overrides
  {
    id: 'tester-away',
    alias: 'priya',
    avatarUrl: null,
    availability: 'away',
    activeJob: null,
    scheduleState: 'on-shift',
    lastActiveAt: ago(15),
    isFloorManager: false,
    devices: ['ios'],
    onBreakUntil: null,
  },
  // on_break — stored availability set by the periodic sweep when an approved
  // step-away covers "now"; the "back at HH:MM" chip surfaces the end time so
  // admins can plan around it without clicking through to the schedule (#4166).
  // Row still buckets into Away per #3667 — chip is in addition to the
  // existing "On break" label.
  {
    id: 'tester-on-break',
    alias: 'rosa',
    avatarUrl: null,
    availability: 'on_break',
    activeJob: null,
    scheduleState: 'on-shift',
    lastActiveAt: ago(8),
    isFloorManager: false,
    devices: ['ios', 'mac'],
    onBreakUntil: new Date(NOW + 22 * 60 * 1000).toISOString(),
  },
  // missing — on-shift but absent past the missing window (60min default)
  {
    id: 'tester-missing',
    alias: 'devon',
    avatarUrl: null,
    availability: 'away',
    activeJob: null,
    scheduleState: 'on-shift',
    lastActiveAt: ago(90),
    isFloorManager: false,
    devices: ['mac'],
    onBreakUntil: null,
  },
  // offline — stored availability, not stale enough for inactive
  {
    id: 'tester-offline',
    alias: 'jules',
    avatarUrl: null,
    availability: 'offline',
    activeJob: null,
    scheduleState: 'off-schedule',
    lastActiveAt: ago(45),
    isFloorManager: false,
    devices: ['pc', 'mac'],
    onBreakUntil: null,
  },
  // inactive — past the inactiveDays threshold (10d default), no devices registered
  {
    id: 'tester-inactive',
    alias: 'alex',
    avatarUrl: null,
    availability: 'offline',
    activeJob: null,
    scheduleState: 'off-schedule',
    lastActiveAt: new Date(NOW - 14 * 24 * 60 * 60 * 1000).toISOString(),
    isFloorManager: false,
    devices: [],
    onBreakUntil: null,
  },
];

/**
 * Default presence thresholds — match the production `/tester/config` shape.
 * Used by the preview harness to seed `<PresenceRoster>`'s timing prop so
 * bucketing renders identically to live data.
 */
export const fixturePresenceTiming: TesterPresenceTimingConfig = {
  autoAwayMinutes: 30,
  autoOfflineMinutes: 240,
  inactiveDays: 10,
  warningMinutes: 1,
};

/**
 * Three co-present participants used by the preview harness to render the
 * `<InstanceParticipantsHeader>` — one of them is the harness "self" so the
 * `(you)` marker renders. Default Discord-CDN avatar URLs (the `embed/avatars`
 * tree) are stable and don't require a custom-asset hash to look right.
 */
export const FIXTURE_PREVIEW_DISCORD_USER_ID = '111111111111111111';

export const fixtureInstanceParticipants: readonly InstanceParticipant[] = [
  {
    id: FIXTURE_PREVIEW_DISCORD_USER_ID,
    displayName: 'admin@example.com',
    avatarUrl: 'https://cdn.discordapp.com/embed/avatars/0.png',
  },
  {
    id: '222222222222222222',
    displayName: 'artur',
    avatarUrl: 'https://cdn.discordapp.com/embed/avatars/2.png',
  },
  {
    id: '333333333333333333',
    displayName: 'morgan',
    avatarUrl: 'https://cdn.discordapp.com/embed/avatars/4.png',
  },
];

/**
 * Two concurrently on-duty FMs (#5169) — both snowflakes match a
 * `fixtureInstanceParticipants` row so the preview harness exercises the
 * "multiple FMs co-present in this Activity" path, with each chip badged by
 * the `<InstanceParticipantsHeader>` and both rows rendered in the tile.
 */
export const fixtureFloorManagersOnDuty: readonly DiscordActivityFloorManagerOnDuty[] = [
  {
    userId: 'user-artur',
    displayName: 'artur',
    discordUserId: '222222222222222222',
    endAt: future(45),
  },
  {
    userId: 'user-morgan',
    displayName: 'morgan',
    discordUserId: '333333333333333333',
    endAt: future(70),
  },
];

export const fixtureStuckJobs: readonly DiscordActivityStuckJob[] = [
  {
    id: 'stuck-aaaa-1111',
    responseDeadline: ago(20),
    assignedTesterName: 'morgan',
    minutesPastDeadline: 20,
  },
  {
    id: 'stuck-bbbb-2222',
    responseDeadline: ago(7),
    assignedTesterName: undefined,
    minutesPastDeadline: 7,
  },
];
